import { useCallback } from "react";
import { useMotionValue, useReducedMotion, useSpring } from "framer-motion";

/**
 * Pointer parallax for the landing hero.
 *
 * The handler is attached to the hero section rather than to the card
 * layer itself: that layer is `pointer-events-none` so it can never
 * intercept a click, and an element that cannot be a pointer target
 * never receives `pointermove` — so a handler down there would never
 * fire. Listening on the section keeps the cards click-through and
 * keeps the depth.
 *
 * Motion is normalised against the viewport, so the lean is the same
 * wherever the pointer is on the page. `reset` returns the stack to
 * neutral; without it the cards would freeze at whatever offset they
 * last reached and stay visibly off-axis for the rest of the visit.
 */
export function usePointerParallax() {
  const reduceMotion = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const smoothX = useSpring(x, { stiffness: 55, damping: 16, mass: 0.7 });
  const smoothY = useSpring(y, { stiffness: 55, damping: 16, mass: 0.7 });

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (reduceMotion) return;
      x.set((event.clientX / window.innerWidth - 0.5) * -26);
      y.set((event.clientY / window.innerHeight - 0.5) * -18);
    },
    [reduceMotion, x, y],
  );

  const reset = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return { smoothX, smoothY, onPointerMove, reset };
}
